import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";

export default function DurationPicker({ onChange, disabled = false }) {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      sx: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 120,
        borderRadius: 3,
        boxShadow: "0 12px 40px rgba(102, 126, 234, 0.15)",
        background:
          "linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.95))",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(102, 126, 234, 0.1)",
        marginTop: 1,
      },
    },
  };

  // Format to HH:MM:SS and send to parent when values change
  useEffect(() => {
    const formatted = [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(seconds).padStart(2, "0"),
    ].join(":");

    if (onChange) onChange(formatted);
  }, [hours, minutes, seconds, onChange]);

  const formControlStyles = {
    fontFamily: "Host Grotesk, sans-serif",
    minWidth: 100,
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))",
      "&:hover": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#667eea",
        },
      },
      "&.Mui-focused": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "#667eea",
          borderWidth: 2,
        },
      },
    },
    "& .MuiInputLabel-root": {
      color: "#64748b",
      fontWeight: 500,
      "&.Mui-focused": {
        color: "#667eea",
      },
    },
  };

  const menuItemStyles = {
    padding: "12px 16px",
    borderRadius: "8px",
    margin: "2px 8px",
    transition: "all 0.2s ease",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#475569",
    "&:hover": {
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
      transform: "translateX(4px)",
    },
    "&.Mui-selected": {
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.15))",
      boxShadow: "0 2px 8px rgba(102, 126, 234, 0.2)",
      fontWeight: 600,
      color: "#1f2937",
      "&:hover": {
        background:
          "linear-gradient(135deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))",
      },
    },
  };

  return (
    <Box>
      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={formControlStyles} disabled={disabled}>
          <InputLabel>Hours</InputLabel>
          <Select
            value={hours}
            label="Hours"
            onChange={(e) => setHours(e.target.value)}
            MenuProps={MenuProps}
          >
            {[...Array(25).keys()].map((h) => (
              <MenuItem key={h} value={h} sx={menuItemStyles}>
                {h}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={formControlStyles} disabled={disabled}>
          <InputLabel>Minutes</InputLabel>
          <Select
            value={minutes}
            label="Minutes"
            onChange={(e) => setMinutes(e.target.value)}
            MenuProps={MenuProps}
          >
            {[...Array(60).keys()].map((m) => (
              <MenuItem key={m} value={m} sx={menuItemStyles}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={formControlStyles} disabled={disabled}>
          <InputLabel>Seconds</InputLabel>
          <Select
            value={seconds}
            label="Seconds"
            onChange={(e) => setSeconds(e.target.value)}
            MenuProps={MenuProps}
          >
            {[...Array(60).keys()].map((s) => (
              <MenuItem key={s} value={s} sx={menuItemStyles}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          mt: 2,
          padding: "12px 16px",
          borderRadius: 2,
          background:
            "linear-gradient(135deg, rgba(102, 126, 234, 0.05), rgba(118, 75, 162, 0.05))",
          border: "1px solid rgba(102, 126, 234, 0.1)",
          display: "inline-block",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            color: "#667eea",
            fontFamily: "Host Grotesk, sans-serif",
          }}
        >
          Duration: {String(hours).padStart(2, "0")}:
          {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </Typography>
      </Box>
    </Box>
  );
}
